import React, { ComponentClass } from 'react';

import { detectBrowser } from 'utils/detectBrower';

const API_KEY = process.env.NEXT_PUBLIC_BUGSNAG_API_KEY;

const shouldReportForBrowser = (): boolean => {
  const browser = detectBrowser();

  if (!browser) {
    return true;
  }

  const { name, version, os } = browser;

  const androidOs = 'Android OS';
  const chromeBrowser = 'chrome';
  const invalidChromeCanaryVersion = '94.0.4590';

  if (name === chromeBrowser && version === invalidChromeCanaryVersion && os === androidOs) {
    return false;
  }

  return true;
};

const shouldReportForUrl = (event): boolean => {
  const invalidUrls = [
    'https://picsartstage2.com',
    'http://picsart.apps',
    'http://picsart.apps:8080',
  ];
  const errorUrl = new URL(event.request.url);

  if (invalidUrls.includes(errorUrl.origin)) {
    return false;
  }

  return true;
};

class ErrorLogger {
  private bugsnagClient = null;

  public async init() {
    if (!API_KEY || typeof window === 'undefined') {
      return;
    }

    const Bugsnag = (await import('@bugsnag/js')).default;
    const BugsnagPluginReact = (await import('@bugsnag/plugin-react')).default;

    this.bugsnagClient = Bugsnag.start({
      apiKey: API_KEY,
      releaseStage: process.env.NEXT_PUBLIC_ENVIRONMENT,
      enabledReleaseStages: ['production', 'preproduction', 'staging'],
      plugins: [new BugsnagPluginReact()],
      onError(event) {
        if (!shouldReportForBrowser()) {
          return false;
        }

        if (!shouldReportForUrl(event)) {
          return false;
        }

        return true;
      },
    });

    return this.bugsnagClient;
  }

  public getErrorBoundaryProvider() {
    const { bugsnagClient } = this;
    return function getErrorBoundary({ children }: { children: JSX.Element | JSX.Element[] }) {
      if (bugsnagClient) {
        const ErrorBoundary = bugsnagClient.getPlugin('react')?.createErrorBoundary(React);

        return React.createElement(ErrorBoundary as ComponentClass, {}, children);
      }

      return children;
    };
  }
}

export default new ErrorLogger();
